import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useConfirm } from 'material-ui-confirm';
import { AppContext } from 'views/App/context';
import { DragStartEvent, DragEndEvent, DragOverEvent } from '@dnd-kit/core';

import { Template, TemplateSection, TemplateField, DragData, SectionFieldOption } from '../_types';
import { Field, FieldOption } from 'views/App/Portal/PerformanceReporting/FieldBank/_types';
import { useTemplate } from '../_data_sources/useTemplate';
import useManageTemplates from '../_data_sources/useManageTemplates';
import { useTemplateAutoSave } from '../_data_sources/useTemplateAutoSave';
import { validateTemplate, getRemovableChildFieldsInLockedSections } from '../_utils/templateValidation';
import { syncTemplateIds } from '../_utils/templateSync';

// Import custom hooks
import { useCollapseState } from './_hooks/useCollapseState';
import { useFieldManagement } from './_hooks/useFieldManagement';
import { useSectionManagement } from './_hooks/useSectionManagement';
import { useDragAndDrop } from './_hooks/useDragAndDrop';
import { useTemplateActions } from './_hooks/useTemplateActions';
import { useNavigationHandlers } from './_hooks/useNavigationHandlers';

export const getFieldUniqueId = (field: TemplateField): string => {
  return field.id === 0 ? `new-${field.fieldId}-${field.templateSectionId}` : `saved-${field.id}`;
};

interface TemplateBuilderContextValue {
  template: Template;
  templateEditable: Template;
  setTemplateEditable: React.Dispatch<React.SetStateAction<Template>>;
  trackActivity: () => void;

  // Loading and error states
  isLoading: boolean;
  isError: boolean;
  error: any;
  isSaving: boolean;
  isTemplateDisabled: boolean;
  autoSaveError: any;
  fieldOptionValidationErrors: Record<string, string>;
  inactiveFieldIds: Set<number>;
  setInactiveFieldIds: (fieldIds: Set<number>) => void;
  clearInactiveFieldIds: () => void;
  duplicateFieldIds: Set<number>;
  setDuplicateFieldIds: (fieldIds: Set<number>) => void;
  clearDuplicateFieldIds: () => void;
  duplicateSectionIds: Set<number>;
  setDuplicateSectionIds: (sectionIds: Set<number>) => void;
  clearDuplicateSectionIds: () => void;
  emptySectionIds: Set<number>;
  setEmptySectionIds: (sectionIds: Set<number>) => void;
  clearEmptySectionIds: () => void;
  removableChildFieldIds: Set<number>;
  setRemovableChildFieldIds: (fieldIds: Set<number>) => void;
  clearRemovableChildFieldIds: () => void;

  // Drag and drop state
  activeId: string | null;
  dragData: DragData | null;
  isOverDropZone: boolean;
  isReorderMode: boolean;

  selectedFieldForEditing: TemplateField | null;
  openFieldOptionsId: string | null; // Tracks unique field identifier (combination of id and templateSectionId for unsaved fields)
  setOpenFieldOptionsId: (fieldId: string | null) => void;

  // Section editing state
  selectedSectionForEditing: TemplateSection | null;

  // Template operations
  addFieldToSection: (field: Field, sectionId: number, sectionGuid?: string) => Promise<void>;
  removeFieldFromSection: (sectionId: number, fieldId: number) => void;
  toggleSectionCollapsed: (sectionId: number) => void;
  addSection: () => void;
  removeSection: (sectionId: number) => Promise<void>;
  toggleReorderMode: () => Promise<void>;
  saveTemplate: (changeNote?: string) => Promise<void>;
  publishTemplate: (changeNote?: string, reviewWorkflowId?: number, updatedBy?: string) => Promise<void>;
  resetTemplate: () => Promise<void>;

  // Drag and drop state
  handleDragStart: (event: DragStartEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleDragOver: (event: DragOverEvent) => void;

  // Field editing operations
  setSelectedFieldForEditing: (field: TemplateField | null) => void;
  updateFieldLabel: (fieldId: number, label: string) => void;
  updateFieldDefaultValue: (fieldId: number, defaultValue: string) => void;
  updateFieldRequired: (fieldId: number, required: boolean) => void;
  updateFieldLocked: (fieldId: number, locked: boolean) => void;
  updateFieldOptions: (fieldId: number, options: SectionFieldOption[]) => void;

  // Input focus tracking
  setInputFieldFocus: (fieldId: string | null, fieldType?: string) => void;
  trackUserActivity: () => void;

  // Auto-save status
  isAutoSaving: boolean;

  // Section editing operations
  setSelectedSectionForEditing: (section: TemplateSection | null) => void;
  updateSectionTitle: (sectionId: number, title: string) => void;
  updateSectionDescription: (sectionId: number, description: string) => void;
  updateSectionRequired: (sectionId: number, required: boolean) => void;
  updateSectionLocked: (sectionId: number, locked: boolean) => void;
  updateSectionRepeatable: (sectionId: number, repeatable: boolean) => void;

  // Navigation
  handleBackToTemplateList: () => void;
  handleEndEditing: () => void;

  // Utility
  sectionIds: string[];
  isValid: boolean;
  isLexipolStaff: boolean;
  isLexipolTemplate: boolean;
}

export const TemplateBuilderContext = createContext<TemplateBuilderContextValue>({} as TemplateBuilderContextValue);

export const TemplateBuilderProvider: React.FC<{ children: ReactNode; initialTemplate?: Template }> = ({
  children,
  initialTemplate,
}) => {
  const { id } = useParams();
  const { currentOrganizationId: orgId, isLexipolStaff } = useContext(AppContext);
  const queryClient = useQueryClient();
  const { updateTemplate, publishTemplate, resetTemplate } = useManageTemplates();
  const confirm = useConfirm();

  // Fetch template by ID if editing an existing template
  const templateId = id ? parseInt(id) : null;

  // Ref to track current template for use in auto-save callback
  const templateEditableRef = useRef<Template | null>(null);
  const selectedSectionRef = useRef<TemplateSection | null>(null);

  // Validation state - declared here before updateBackendTemplate callback that uses them
  const [inactiveFieldIds, setInactiveFieldIdsState] = useState<Set<number>>(new Set());
  const [duplicateFieldIds, setDuplicateFieldIdsState] = useState<Set<number>>(new Set());
  const [duplicateSectionIds, setDuplicateSectionIdsState] = useState<Set<number>>(new Set());
  const [emptySectionIds, setEmptySectionIdsState] = useState<Set<number>>(new Set());
  const [removableChildFieldIds, setRemovableChildFieldIdsState] = useState<Set<number>>(new Set());

  // Callback to update the backend template when auto-save succeeds
  const updateBackendTemplate = useCallback(
    (updatedAt: string, savedTemplate?: Template) => {
      if (savedTemplate) {
        setTemplate(savedTemplate);

        const currentTemplate = templateEditableRef.current;
        if (!currentTemplate) return;

        const { updatedTemplate, sectionGuidMap } = syncTemplateIds(currentTemplate, savedTemplate);
        setTemplateEditable(updatedTemplate);

        // Update selected section if it had id: 0 (was a new section)
        const currentSelectedSection = selectedSectionRef.current;
        if (currentSelectedSection && currentSelectedSection.id === 0) {
          // Use the sectionGuidMap to find the new sectionGuid assigned to this section
          const newSectionGuid = sectionGuidMap.get(currentSelectedSection.sectionGuid);
          if (newSectionGuid) {
            const updatedSection = updatedTemplate.templateSections.find((s) => s.sectionGuid === newSectionGuid);
            if (updatedSection) {
              setSelectedSectionForEditingState(updatedSection);
            }
          }
        }

        // Re-validate only if there are already highlighted issues (from a publish attempt)
        // This provides real-time feedback when fixing issues without running validation on every save
        const hasValidationIssues =
          inactiveFieldIds.size > 0 ||
          duplicateFieldIds.size > 0 ||
          duplicateSectionIds.size > 0 ||
          emptySectionIds.size > 0 ||
          removableChildFieldIds.size > 0;

        if (hasValidationIssues) {
          const validationResults = validateTemplate(updatedTemplate);
          setInactiveFieldIdsState(validationResults.inactiveFieldIds);
          setDuplicateFieldIdsState(validationResults.duplicateFieldIds);
          setDuplicateSectionIdsState(validationResults.duplicateSectionIds);
          setEmptySectionIdsState(validationResults.emptySectionIds);
          setRemovableChildFieldIdsState(validationResults.removableChildFieldIds);
        }
      } else {
        // Fallback to just updating timestamp if no full template provided
        setTemplate((prev) => ({
          ...prev,
          updatedAt,
        }));
      }
    },
    [inactiveFieldIds, duplicateFieldIds, duplicateSectionIds, emptySectionIds]
  );

  // Auto-save functionality with optimistic updates
  const {
    triggerAutoSave,
    trackUserActivity,
    setInputFieldFocus,
    isAutoSaving,
    autoSaveError,
    cancelAutoSave,
    enforceAutoSave,
  } = useTemplateAutoSave(templateId || 0, updateBackendTemplate);
  const {
    data: fetchedTemplate,
    isLoading: isFetchingTemplate,
    error: fetchError,
  } = useTemplate(
    templateId || 0,
    {
      enabled: !!templateId,
      refetchOnMount: 'always',
    },
    (error) => {
      console.error('Error fetching template:', error);
      setIsError(true);
      setError(error);
    },
    true
  );

  // Determine which template to use (fetched, initial, or default)
  const sourceTemplate = fetchedTemplate || initialTemplate;

  // Backend template state (source of truth from server)
  const [template, setTemplate] = useState<Template>(() => ({
    id: sourceTemplate?.id || 0,
    name: sourceTemplate?.name || 'Crime Report',
    versionId: sourceTemplate?.versionId || '1',
    assignedModuleId: sourceTemplate?.assignedModuleId || 1,
    description: sourceTemplate?.description || '',
    effectiveDate: sourceTemplate?.effectiveDate || new Date().toISOString(),
    publishedBy: sourceTemplate?.publishedBy || '',
    lastPublishDate: sourceTemplate?.lastPublishDate || '',
    createdAt: sourceTemplate?.createdAt || new Date().toISOString(),
    createdBy: sourceTemplate?.createdBy || 'current-user',
    updatedAt: sourceTemplate?.updatedAt || new Date().toISOString(),
    updatedBy: sourceTemplate?.updatedBy || 'current-user',
    status: sourceTemplate?.status || 'Draft',
    templateSections: sourceTemplate?.templateSections?.map((section, index) => ({
      ...section,
      sectionFields: section.sectionFields || [], // Ensure sectionFields array exists
      collapsed: section.collapsed ?? false,
      displayOrder: section.displayOrder ?? index + 1,
      // Default Effective* properties for backward compatibility
      sectionGuid: section.sectionGuid ?? `section-${section.id}`,
      parentSectionGuid: section.parentSectionGuid ?? null,
      effectiveName: section.effectiveName ?? section.name ?? 'Unnamed Section',
      effectiveDescription: section.effectiveDescription ?? section.description ?? '',
      effectiveIsRequired: section.effectiveIsRequired ?? false,
      effectiveIsLocked: section.effectiveIsLocked ?? false,
      effectiveIsRepeatable: section.effectiveIsRepeatable ?? false,
      effectiveIsDeleted: section.effectiveIsDeleted ?? false,
      isDeleted: section.isDeleted !== undefined ? section.isDeleted : false,
    })) || [
      {
        id: 0, // Backend will assign ID when saved
        templateVersionId: parseInt(sourceTemplate?.versionId || '1'),
        sectionGuid: 'event-details-guid',
        parentSectionGuid: null,
        name: 'Event Details',
        effectiveName: 'Event Details',
        description: '',
        effectiveDescription: '',
        createdAt: new Date().toISOString(),
        createdBy: 'current-user',
        updatedAt: new Date().toISOString(),
        updatedBy: 'current-user',
        sectionFields: [],
        collapsed: false,
        displayOrder: 1,
        isRequired: false,
        effectiveIsRequired: false,
        isLocked: false,
        effectiveIsLocked: false,
        isRepeatable: false,
        effectiveIsRepeatable: false,
        isDeleted: false,
        effectiveIsDeleted: false,
        parentTemplateSectionId: null,
        parentTemplateSection: null,
      },
    ],
    parentTemplateId: sourceTemplate?.parentTemplateId || null,
    parentVersionId: sourceTemplate?.parentVersionId || null,
    parentTemplate: sourceTemplate?.parentTemplate || null,
    jurisdictionId: sourceTemplate?.jurisdictionId,
  }));

  const [templateEditable, setTemplateEditable] = useState<Template>(() => template);
  const lastActivityRef = useRef<number>(Date.now());
  const templateRef = useRef(template);

  const trackActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    trackUserActivity(); // Also track for auto-save
  }, [trackUserActivity]);

  // Keep refs in sync with state for use in updateBackendTemplate callback
  useEffect(() => {
    templateEditableRef.current = templateEditable;
  }, [templateEditable]);

  // Update ref when template changes (but don't restart interval)
  useEffect(() => {
    templateRef.current = template;
  }, [template]);

  // Stable 30-second sync interval (never restarts)
  useEffect(() => {
    const interval = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;

      // If user has been inactive for 30 seconds, sync backend template to editable
      if (timeSinceLastActivity >= 30000) {
        const currentTemplate = templateRef.current;
        setTemplateEditable({ ...currentTemplate });
        lastActivityRef.current = Date.now();
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(interval);
  }, []);

  // Update templateEditable when template changes from backend (initial load, etc.)
  useEffect(() => {
    if (fetchedTemplate) {
      setTemplate(fetchedTemplate);
      setTemplateEditable(fetchedTemplate);
    }
  }, [fetchedTemplate]);

  // Track if user has made any edits (for optimistic UI)
  const [hasUserEdits, setHasUserEdits] = useState(false);
  const hasUserEditsRef = useRef(false);

  // Update template when fetched template arrives (only on initial load, not during editing)
  useEffect(() => {
    if (fetchedTemplate && templateId && !hasUserEditsRef.current) {
      // Only update from backend if user hasn't made edits (fresh load)
      const templateWithDefaults = {
        ...fetchedTemplate,
        templateSections:
          fetchedTemplate.templateSections?.map((section, index) => ({
            ...section,
            sectionFields: section.sectionFields || [],
            collapsed: section.collapsed ?? false,
            displayOrder: section.displayOrder ?? index + 1,
          })) || [],
      };
      setTemplate(templateWithDefaults);
    } else if (fetchedTemplate && templateId && hasUserEditsRef.current) {
      console.log('Template data received from backend but ignored - preserving user edits');
    }
  }, [fetchedTemplate, templateId]);

  // Section editing state - store the entire section object (like fields) for consistency
  const [selectedSectionForEditing, setSelectedSectionForEditingState] = useState<TemplateSection | null>(null);

  // Keep selected section ref in sync
  useEffect(() => {
    selectedSectionRef.current = selectedSectionForEditing;
  }, [selectedSectionForEditing]);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<any>(null);

  // Reorder mode state
  const [isReorderMode, setIsReorderMode] = useState(false);

  // Field editing state
  const [selectedFieldForEditing, setSelectedFieldForEditingState] = useState<TemplateField | null>(null);

  // Field options panel state (for floating gear/delete buttons)
  const [openFieldOptionsId, setOpenFieldOptionsIdState] = useState<string | null>(null);

  // isSaving state for UI feedback
  const [isSaving, setIsSaving] = useState(false);

  // Validation state helper functions (state declared earlier before updateBackendTemplate)
  // Template disabled state for critical operations (publish, reset, end editing)
  const [isTemplateDisabled, setIsTemplateDisabled] = useState(false);

  // Inactive field highlighting helper functions
  const setInactiveFieldIds = useCallback((fieldIds: Set<number>) => {
    setInactiveFieldIdsState(fieldIds);
  }, []);

  const clearInactiveFieldIds = useCallback(() => {
    setInactiveFieldIdsState(new Set());
  }, []);

  const setDuplicateFieldIds = useCallback((fieldIds: Set<number>) => {
    setDuplicateFieldIdsState(fieldIds);
  }, []);

  const clearDuplicateFieldIds = useCallback(() => {
    setDuplicateFieldIdsState(new Set());
  }, []);

  const setDuplicateSectionIds = useCallback((sectionIds: Set<number>) => {
    setDuplicateSectionIdsState(sectionIds);
  }, []);

  const clearDuplicateSectionIds = useCallback(() => {
    setDuplicateSectionIdsState(new Set());
  }, []);

  const setEmptySectionIds = useCallback((sectionIds: Set<number>) => {
    setEmptySectionIdsState(sectionIds);
  }, []);

  const clearEmptySectionIds = useCallback(() => {
    setEmptySectionIdsState(new Set());
  }, []);

  // Removable child fields helper functions
  const setRemovableChildFieldIds = useCallback((fieldIds: Set<number>) => {
    setRemovableChildFieldIdsState(fieldIds);
  }, []);

  const clearRemovableChildFieldIds = useCallback(() => {
    setRemovableChildFieldIdsState(new Set());
  }, []);

  // Field option validation errors
  const [fieldOptionValidationErrors, setFieldOptionValidationErrors] = useState<Record<string, string>>({});

  // Helper function to mark that user has started editing
  const markUserEditing = useCallback(() => {
    if (!hasUserEditsRef.current) {
      hasUserEditsRef.current = true;
      setHasUserEdits(true);
      console.log('User editing started - local state is now source of truth');
    }
  }, []);

  // Helper function to reset editing state (for navigation/refresh)
  const resetEditingState = useCallback(() => {
    hasUserEditsRef.current = false;
    setHasUserEdits(false);
    console.log('Editing state reset - ready to load from backend');
  }, []);

  // Function to load latest saved version (for manual sync)
  const loadLatestSavedVersion = useCallback(() => {
    const latestSaved = queryClient.getQueryData<Template>(['template-saved', templateId]);
    if (latestSaved) {
      setTemplate(latestSaved);
      resetEditingState();
      console.log('Loaded latest saved version from cache');
    } else {
      // Refetch from server if no cached saved version
      queryClient.invalidateQueries(['template', templateId]);
      console.log('Refetching latest version from server');
    }
  }, [queryClient, templateId, resetEditingState]);

  // Cleanup auto-save on unmount
  useEffect(() => {
    return () => {
      cancelAutoSave();
    };
  }, [cancelAutoSave]);

  // Enhanced setters that handle mutual exclusivity
  const setSelectedFieldForEditing = useCallback((field: TemplateField | null) => {
    setSelectedFieldForEditingState(field);
    // Clear section selection when field is selected
    if (field) {
      setSelectedSectionForEditingState(null);
    }
  }, []);

  // Field options panel management
  const setOpenFieldOptionsId = useCallback((fieldId: string | null) => {
    setOpenFieldOptionsIdState(fieldId);
  }, []);

  const setSelectedSectionForEditing = useCallback((section: TemplateSection | null) => {
    setSelectedSectionForEditingState(section);
    // Clear field selection when section is selected
    if (section) {
      setSelectedFieldForEditingState(null);
    }
    // Close any open field options panel when selecting a section
    setOpenFieldOptionsIdState(null);
  }, []);

  // Use custom hooks for different operations
  // Use templateEditable instead of template so collapse state matches the IDs being displayed
  const collapseState = useCollapseState(templateEditable, templateId, isReorderMode);

  // Computed template type
  const isLexipolTemplate = useMemo(() => {
    // Lexipol/Global templates have organizationId === null
    // Agency templates have organizationId !== null
    return template.organizationId === null;
  }, [template.organizationId]);

  const fieldManagement = useFieldManagement(
    templateEditable,
    setTemplateEditable,
    trackActivity,
    triggerAutoSave,
    selectedFieldForEditing,
    setSelectedFieldForEditingState,
    openFieldOptionsId,
    setOpenFieldOptionsIdState,
    setFieldOptionValidationErrors,
    removableChildFieldIds,
    duplicateFieldIds,
    inactiveFieldIds
  );

  const sectionManagement = useSectionManagement(
    templateEditable,
    setTemplateEditable,
    trackActivity,
    triggerAutoSave,
    collapseState.collapsedSections,
    collapseState.expandSections,
    collapseState.collapseSections,
    selectedFieldForEditing,
    setSelectedFieldForEditingState,
    selectedSectionForEditing,
    setSelectedSectionForEditingState,
    openFieldOptionsId,
    setOpenFieldOptionsIdState,
    template,
    confirm
  );

  const dragAndDrop = useDragAndDrop(
    templateEditable,
    setTemplateEditable,
    trackActivity,
    triggerAutoSave,
    isReorderMode,
    fieldManagement.addFieldToSection,
    fieldManagement.addFieldToSectionAtIndex,
    setOpenFieldOptionsIdState,
    template.parentTemplate
  );

  const templateActions = useTemplateActions(
    template,
    templateEditable,
    setIsSaving,
    updateTemplate,
    publishTemplate,
    resetTemplate,
    triggerAutoSave,
    enforceAutoSave,
    isReorderMode,
    setIsReorderMode,
    setTemplate,
    setTemplateEditable,
    clearInactiveFieldIds,
    setInactiveFieldIds,
    clearDuplicateFieldIds,
    setDuplicateFieldIds,
    clearDuplicateSectionIds,
    setDuplicateSectionIds,
    clearEmptySectionIds,
    setEmptySectionIds,
    clearRemovableChildFieldIds,
    setRemovableChildFieldIds,
    setIsTemplateDisabled,
    isLexipolTemplate,
    isLexipolStaff
  );

  const navigationHandlers = useNavigationHandlers(
    template,
    resetEditingState,
    enforceAutoSave,
    templateEditable,
    setIsTemplateDisabled
  );

  // Computed values - convert to strings for dnd-kit compatibility
  // Use hybrid ID strategy: sectionGuid for new sections (id: 0), otherwise use id
  const sectionIds = useMemo(
    () => template.templateSections.map((s) => (s.id === 0 ? `temp-section-${s.sectionGuid}` : String(s.id))),
    [template.templateSections]
  );

  const isValid = useMemo(() => {
    // Basic validation: template must have name and at least one non-deleted section
    const activeSections = template.templateSections.filter((s) => !s.isDeleted && !s.effectiveIsDeleted);
    return template.name.trim() !== '' && activeSections.length > 0;
  }, [template]);

  // Always expose a UI-applied template to consumers
  const uiTemplate = useMemo(
    () => collapseState.applyCollapseMap(templateEditable),
    [templateEditable, collapseState.applyCollapseMap]
  );

  const value: TemplateBuilderContextValue = useMemo(
    () => ({
      // Template states
      template, // Backend template (for "Last saved" and comparisons)
      templateEditable: uiTemplate, // UI template with collapse state applied
      setTemplateEditable,
      trackActivity,

      // Loading and error states
      isLoading: isLoading || isFetchingTemplate,
      isError: isError || !!fetchError,
      error: error || fetchError,
      isSaving: isSaving || isAutoSaving,
      isTemplateDisabled,
      autoSaveError,
      fieldOptionValidationErrors,

      // Inactive field highlighting
      inactiveFieldIds,
      setInactiveFieldIds,
      clearInactiveFieldIds,

      // Duplicate field highlighting
      duplicateFieldIds,
      setDuplicateFieldIds,
      clearDuplicateFieldIds,

      // Duplicate section highlighting
      duplicateSectionIds,
      setDuplicateSectionIds,
      clearDuplicateSectionIds,

      // Empty section highlighting
      emptySectionIds,
      setEmptySectionIds,
      clearEmptySectionIds,

      // Removable child fields in locked sections
      removableChildFieldIds,
      setRemovableChildFieldIds,
      clearRemovableChildFieldIds,

      // Drag and drop state
      activeId: dragAndDrop.activeId,
      dragData: dragAndDrop.dragData,
      isOverDropZone: dragAndDrop.isOverDropZone,
      isReorderMode,

      // Field editing state
      selectedFieldForEditing,

      // Field options panel state
      openFieldOptionsId,
      setOpenFieldOptionsId,

      // Section editing state
      selectedSectionForEditing,

      // Template operations
      addFieldToSection: fieldManagement.addFieldToSection,
      removeFieldFromSection: fieldManagement.removeFieldFromSection,
      toggleSectionCollapsed: sectionManagement.toggleSectionCollapsed,
      addSection: sectionManagement.addSection,
      removeSection: sectionManagement.removeSection,
      toggleReorderMode: templateActions.toggleReorderMode,
      saveTemplate: templateActions.saveTemplate,
      publishTemplate: templateActions.publishTemplate,
      resetTemplate: templateActions.resetTemplate,

      // Field editing operations
      setSelectedFieldForEditing,
      updateFieldLabel: fieldManagement.updateFieldLabel,
      updateFieldDefaultValue: fieldManagement.updateFieldDefaultValue,
      updateFieldRequired: fieldManagement.updateFieldRequired,
      updateFieldLocked: fieldManagement.updateFieldLocked,
      updateFieldOptions: fieldManagement.updateFieldOptions,

      // Input focus tracking
      setInputFieldFocus,
      trackUserActivity,

      // Auto-save status
      isAutoSaving,

      // Section editing operations
      setSelectedSectionForEditing,
      updateSectionTitle: sectionManagement.updateSectionTitle,
      updateSectionDescription: sectionManagement.updateSectionDescription,
      updateSectionRequired: sectionManagement.updateSectionRequired,
      updateSectionLocked: sectionManagement.updateSectionLocked,
      updateSectionRepeatable: sectionManagement.updateSectionRepeatable,

      // Drag operations
      handleDragStart: dragAndDrop.handleDragStart,
      handleDragEnd: dragAndDrop.handleDragEnd,
      handleDragOver: dragAndDrop.handleDragOver,

      // Navigation
      handleBackToTemplateList: navigationHandlers.handleBackToTemplateList,
      handleEndEditing: navigationHandlers.handleEndEditing,

      // Utility
      sectionIds,
      isValid,

      // User permissions
      isLexipolStaff,
      isLexipolTemplate,
    }),
    [
      uiTemplate,
      isLoading,
      isError,
      error,
      isSaving,
      isTemplateDisabled,
      dragAndDrop.activeId,
      dragAndDrop.dragData,
      isReorderMode,
      selectedFieldForEditing,
      openFieldOptionsId,
      setOpenFieldOptionsId,
      selectedSectionForEditing,
      fieldManagement.addFieldToSection,
      fieldManagement.removeFieldFromSection,
      sectionManagement.toggleSectionCollapsed,
      sectionManagement.addSection,
      sectionManagement.removeSection,
      templateActions.toggleReorderMode,
      templateActions.saveTemplate,
      templateActions.publishTemplate,
      setSelectedFieldForEditing,
      fieldManagement.updateFieldLabel,
      fieldManagement.updateFieldDefaultValue,
      fieldManagement.updateFieldRequired,
      fieldManagement.updateFieldLocked,
      fieldManagement.updateFieldOptions,
      setInputFieldFocus,
      setSelectedSectionForEditing,
      sectionManagement.updateSectionTitle,
      sectionManagement.updateSectionDescription,
      sectionManagement.updateSectionRequired,
      sectionManagement.updateSectionLocked,
      sectionManagement.updateSectionRepeatable,
      dragAndDrop.handleDragStart,
      dragAndDrop.handleDragEnd,
      navigationHandlers.handleBackToTemplateList,
      navigationHandlers.handleEndEditing,
      sectionIds,
      isValid,
      isAutoSaving,
      fieldOptionValidationErrors,
      isFetchingTemplate,
      fetchError,
      autoSaveError,
      template,
      trackActivity,
      trackUserActivity,
      inactiveFieldIds,
      setInactiveFieldIds,
      clearInactiveFieldIds,
      duplicateFieldIds,
      setDuplicateFieldIds,
      clearDuplicateFieldIds,
      duplicateSectionIds,
      setDuplicateSectionIds,
      clearDuplicateSectionIds,
      emptySectionIds,
      setEmptySectionIds,
      clearEmptySectionIds,
      removableChildFieldIds,
      isLexipolStaff,
      isLexipolTemplate,
    ]
  );

  return <TemplateBuilderContext.Provider value={value}>{children}</TemplateBuilderContext.Provider>;
};

export const useTemplateBuilderContext = () => {
  const context = useContext(TemplateBuilderContext);
  if (!context) {
    throw new Error('useTemplateBuilderContext must be used within a TemplateBuilderProvider');
  }
  return context;
};
